/**
 * What one bar should show on one animation frame.
 *
 * Kept pure and DOM-free: the render loop calls this per string per frame and
 * then only writes the result out, so all the behaviour can be tested without
 * a browser.
 */

import { snapToNote } from '../../music/snap'
import {
  advanceEma,
  afterglowOpacity,
  fadeStateFor,
  needleFraction,
  zoneFor,
  type FadeState,
  type TuningZone,
} from './bar-math'

/** Everything needed to draw one bar for one frame. */
export interface BarFrameInput {
  /** Last measured fundamental in Hz, or null when never measured. */
  readonly frequency: number | null
  /** Timestamp of that measurement, or null when never measured. */
  readonly updatedAt: number | null
  /** MIDI note the string is tuned to. */
  readonly targetMidi: number
  /** Reference pitch in Hz. */
  readonly a4: number
  /** Current frame time in milliseconds. */
  readonly now: number
  /** Smoothed cents from the previous frame, or null to start fresh. */
  readonly previousCents: number | null
  /** Note shown on the previous frame, for snap hysteresis. */
  readonly previousMidi: number | null
  /** Time since the previous frame in milliseconds. */
  readonly deltaMs: number
}

/** Drawing instructions for one bar. */
export interface BarFrameOutput {
  /** MIDI note to display, or null when there is nothing to show. */
  readonly midi: number | null
  /** Smoothed deviation in cents, or null when there is nothing to show. */
  readonly cents: number | null
  /** Needle position in [-1, 1]; 0 when idle. */
  readonly fraction: number
  /** Colour band. */
  readonly zone: TuningZone
  /** Whether the bar is live, fading, or dormant. */
  readonly fade: FadeState
  /** Fade-out factor in [0, 1] across the afterglow window. */
  readonly glow: number
  /** True when the displayed note is not the string's target. */
  readonly isSnapped: boolean
}

/** A bar with no reading to show: centred, neutral, dark. */
const IDLE: BarFrameOutput = {
  midi: null,
  cents: null,
  fraction: 0,
  zone: 'in-tune',
  fade: 'idle',
  glow: 0,
  isSnapped: false,
}

/**
 * Resolve one bar for one frame.
 *
 * A string that has gone quiet keeps its last reading for the afterglow
 * window, still smoothing towards it, then falls back to the idle state.
 */
export function computeBarFrame(input: BarFrameInput): BarFrameOutput {
  const { frequency, updatedAt, now } = input

  if (frequency === null || updatedAt === null) return IDLE

  const ageMs = Math.max(0, now - updatedAt)
  const fade = fadeStateFor(ageMs)
  if (fade === 'idle') return IDLE

  const snapped = snapToNote(frequency, input.targetMidi, input.a4, input.previousMidi)
  const cents = advanceEma(input.previousCents, snapped.cents, input.deltaMs)

  return {
    midi: snapped.midi,
    cents,
    fraction: needleFraction(cents),
    zone: zoneFor(cents),
    fade,
    glow: afterglowOpacity(ageMs),
    isSnapped: snapped.isSnapped,
  }
}
